import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast';


// Context Provider
import { AuthProvider } from './contexts/AuthContext';
import { GrievanceProvider } from './contexts/GrievanceContexts';
import { LanguageProvider } from './contexts/LanguageContext';
import { SocketProvider } from './contexts/SocketContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { NotificationProvider } from './contexts/NotificationContext';



// Layouts
import PublicLayout from './layouts/PublicLayout';
import AdminLayout from './layouts/SuperAdminLayout';
import OfficerLayout from './layouts/MinisterAdminLayout';
import OfficeAdminLayout from './layouts/OfficeAdminLayout';

// Public Pages
import HomePage from './pages/public/HomePage';
import SubmitGrievancePage from './pages/public/SubmitGrievancePage';
import TrackGrievancePage from './pages/public/TrackGrievancePage';
import PublicStatsPage from './pages/public/PublicStatsPage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// SuperAdmin Pages
import AdminDashboard from './pages/super_admin/Dashboard';
import AdminGrievances from './pages/super_admin/Grievances';
import AdminUsers from './pages/super_admin/Users';
import AdminCategories from './pages/super_admin/Categories';
import AdminDepartments from './pages/super_admin/Departments';
import AdminReports from './pages/super_admin/Reports';
import AdminReviews from './pages/super_admin/Reviews';
import AdminProfile from './pages/super_admin/Profile';
import SiteSettings from './pages/super_admin/SiteSettings';

// MinisterAdmin Pages
import OfficerDashboard from './pages/minster_admin/Dashboard';
import OfficerGrievances from './pages/minster_admin/Grievances';
import OfficerProfile from './pages/minster_admin/Profile';
import OfficerReviews from './pages/minster_admin/Reviews';
import OfficerOffices from './pages/office_admin/Offices';

// Office Admin Pages
import OfficeDashboard from './pages/office_admin/Dashboard';
import OfficeGrievances from './pages/office_admin/Grievances';
import OfficeProfile from './pages/office_admin/Profile';
import OfficeReviews from './pages/office_admin/Reviews';
import OfficeReports from './pages/office_admin/Reports';

// Protected Route Component
import ProtectedRoute from './components/auth/ProtectedRoute';


// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <Router>
          <GrievanceProvider>
            <SettingsProvider>

              <AuthProvider>
                <NotificationProvider>
                  <SocketProvider>
                    <div className="min-h-screen bg-gray-50">
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/" element={<PublicLayout />}>
                        <Route index element={<HomePage />} />
                        <Route path="submit" element={<SubmitGrievancePage />} />
                        <Route path="track" element={<TrackGrievancePage />} />
                        <Route path="track/:grievanceId" element={<TrackGrievancePage />} />
                        <Route path="stats" element={<PublicStatsPage />} />
                      </Route>

                      {/* Auth Routes */}
                      <Route path="/auth" element={<PublicLayout />}>
                        <Route path="login" element={<LoginPage />} />
                        <Route path="register" element={<RegisterPage />} />
                        <Route path="forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="reset-password" element={<ResetPasswordPage />} />
                      </Route>

                      {/* Super Admin Routes */}
                      <Route
                        path="/super-admin"
                        element={
                          <ProtectedRoute requiredRole="SUPER_ADMIN">
                            <AdminLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route path="dashboard" element={<AdminDashboard />} />
                        <Route path="grievances" element={<AdminGrievances />} />
                        <Route path="reviews" element={<AdminReviews />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="categories" element={<AdminCategories />} />
                        <Route path="departments" element={<AdminDepartments />} />
                        <Route path="reports" element={<AdminReports />} />
                        <Route path="profile" element={<AdminProfile />} />
                        <Route path="sitesettings" element={<SiteSettings />} />

                      </Route>

                      {/* Ministry Admin Routes */}
                      <Route
                        path="/ministry"
                        element={
                          <ProtectedRoute requiredRole="MINISTRY_ADMIN">
                            <OfficerLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route path="dashboard" element={<OfficerDashboard />} />
                        <Route path="grievances" element={<OfficerGrievances />} />
                        <Route path="offices" element={<OfficerOffices />} />
                        <Route path="profile" element={<OfficerProfile />} />
                        <Route path="reviews" element={<OfficerReviews />} />
                      </Route>

                      {/* Office Admin Routes */}
                      <Route
                        path="/office"
                        element={
                          <ProtectedRoute requiredRole="OFFICE_ADMIN">
                            <OfficeAdminLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route path="dashboard" element={<OfficeDashboard />} />
                        <Route path="grievances" element={<OfficeGrievances />} />
                        <Route path="reviews" element={<OfficeReviews />} />
                        <Route path="reports" element={<OfficeReports />} />
                        <Route path="profile" element={<OfficeProfile />} />
                      </Route>

                      {/* 404 Route */}
                      <Route path="*" element={<div>404 - Page Not Found</div>} />
                    </Routes>

                    {/* Global Toaster */}
                    <Toaster
                      position="top-right"
                      toastOptions={{
                        duration: 4000,
                        style: {
                          background: '#363636',
                          color: '#fff',
                        },
                        success: {
                          duration: 3000,
                          iconTheme: {
                            primary: '#22c55e',
                            secondary: '#fff',
                          },
                        },
                        error: {
                          duration: 5000,
                          iconTheme: {
                            primary: '#ef4444',
                            secondary: '#fff',
                          },
                        },
                      }}
                    />
                  </div>
                </SocketProvider>
              </NotificationProvider>
              </AuthProvider>
            </SettingsProvider>
          </GrievanceProvider>
        </Router>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;
